import dompurify from 'dompurify'
import type { RouteLocationRaw } from 'vue-router'
import { FavouriteType } from '@/stores/favourites'
import { generateSlug } from '~/utils/slug'

export const PORTAL_SEARCH_ENTITY_TYPES = [
  'risk-assessment',
  'accepted-feed-material',
  'processing-aid',
  'undesirable-substance',
  'certified-company',
  'document',
] as const

export type PortalSearchEntityType = (typeof PORTAL_SEARCH_ENTITY_TYPES)[number]

export type PortalSearchFavouriteFilter = {
  entityType: PortalSearchEntityType
  ids: string[]
}

export type PortalSearchCriteria = {
  term: string
  language: string
  selectedTypes: PortalSearchEntityType[]
  favouritesOnly: boolean
  favouriteFilters: PortalSearchFavouriteFilter[]
}

export type PortalSearchResult = {
  id: string
  entityType: string
  title: string
  summary?: string
  sourceKey?: string
  tags?: string[]
  code?: string
  category?: string
  highlightTitle?: string
  highlightSummary?: string
}

export type PortalSearchResultRaw = Omit<
  PortalSearchResult,
  'highlightTitle' | 'highlightSummary'
> & {
  '@search.highlights'?: Record<string, string[]>
}

export type PortalSearchResponse = {
  value: PortalSearchResultRaw[]
  '@odata.count'?: number
}

export type PortalSearchFacetsResponse = {
  '@search.facets'?: {
    entityType?: Array<{
      value: string
      count: number
    }>
  }
}

export type PortalSearchRequest = {
  search: string
  searchMode: 'any'
  queryType: 'full'
  searchFields: string
  highlightPreTag?: '<em>'
  highlightPostTag?: '</em>'
  highlight?: string
  facets?: string[]
  top: number
  skip: number
  count: boolean
  filter: string
}

export type PortalSearchHighlightSegment = {
  text: string
  emphasized: boolean
}

export const PORTAL_SEARCH_FAVOURITE_MAPPINGS = [
  { kind: FavouriteType.product, entityType: 'accepted-feed-material' },
  { kind: FavouriteType.document, entityType: 'document' },
  { kind: FavouriteType.riskAssessment, entityType: 'risk-assessment' },
  { kind: FavouriteType.hazard, entityType: 'undesirable-substance' },
  { kind: FavouriteType.processingAid, entityType: 'processing-aid' },
  { kind: FavouriteType.location, entityType: 'certified-company' },
] as const satisfies ReadonlyArray<{
  kind: FavouriteType
  entityType: PortalSearchEntityType
}>

const PORTAL_SEARCH_ENTITY_ICONS: Record<PortalSearchEntityType, string> = {
  'risk-assessment': 'custom:risk-assessments',
  'accepted-feed-material': 'mdi:package-variant-closed',
  'processing-aid': 'mdi:beaker',
  'undesirable-substance': 'mdi:alert-octagon-outline',
  'certified-company': 'ic:baseline-domain',
  document: 'mdi:file-document-outline',
}

const SEARCH_FIELDS = 'title,summary,content,tags,code'
const HIGHLIGHT_FIELDS = 'title,summary,content,tags,code'

export const sanitizePortalSearchTerm = (value: string) =>
  value
    .replace(/([+\-&|!(){}[\]^"~*?:\\/])/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const escapeODataValue = (value: string) => value.replaceAll("'", "''")

const createFavouritesFilter = (criteria: PortalSearchCriteria) => {
  if (!criteria.favouritesOnly) return ''
  if (!criteria.favouriteFilters.length) return '1 eq 0'

  const segments = criteria.favouriteFilters.map(({ entityType, ids }) => {
    const safeIds = ids.map(escapeODataValue).join(',')
    return `(entityType eq '${entityType}' and search.in(sourceKey, '${safeIds}', ','))`
  })

  return segments.length > 1 ? `(${segments.join(' or ')})` : segments[0]
}

export const createPortalSearchFilter = (
  criteria: PortalSearchCriteria,
  includeSelectedTypes: boolean,
) => {
  const filters = [`language eq '${escapeODataValue(criteria.language)}'`]

  if (includeSelectedTypes && criteria.selectedTypes.length) {
    filters.push(`search.in(entityType, '${criteria.selectedTypes.join(',')}', ',')`)
  }

  const favouritesFilter = createFavouritesFilter(criteria)
  if (favouritesFilter) filters.push(favouritesFilter)

  return filters.join(' and ')
}

const createSearchExpression = (term: string) => {
  const safeTerm = sanitizePortalSearchTerm(term.trim())
  return safeTerm ? `${safeTerm}*` : '*'
}

export const createPortalSearchResultsRequest = (
  criteria: PortalSearchCriteria,
  skip: number,
  pageSize: number,
): PortalSearchRequest => ({
  search: createSearchExpression(criteria.term),
  searchMode: 'any',
  queryType: 'full',
  searchFields: SEARCH_FIELDS,
  highlightPreTag: '<em>',
  highlightPostTag: '</em>',
  highlight: HIGHLIGHT_FIELDS,
  top: pageSize,
  skip,
  count: true,
  filter: createPortalSearchFilter(criteria, true),
})

export const createPortalSearchFacetsRequest = (
  criteria: PortalSearchCriteria,
): PortalSearchRequest => ({
  search: createSearchExpression(criteria.term),
  searchMode: 'any',
  queryType: 'full',
  searchFields: SEARCH_FIELDS,
  facets: ['entityType,count:10'],
  top: 0,
  skip: 0,
  count: false,
  filter: createPortalSearchFilter(criteria, false),
})

export const mapPortalSearchResult = (item: PortalSearchResultRaw): PortalSearchResult => {
  const highlights = item['@search.highlights'] ?? {}

  return {
    ...item,
    highlightTitle: highlights.title?.[0] ?? item.title,
    highlightSummary: highlights.summary?.[0] ?? highlights.content?.[0] ?? item.summary,
  }
}

export const getPortalSearchEntityIcon = (entityType: string) =>
  PORTAL_SEARCH_ENTITY_ICONS[entityType as PortalSearchEntityType] ?? 'mdi:magnify'

export const getPortalSearchEntityLabelKey = (entityType: string) =>
  PORTAL_SEARCH_ENTITY_TYPES.includes(entityType as PortalSearchEntityType)
    ? `globalsearch.filters.${entityType}`
    : entityType

export const resolvePortalSearchLocation = (
  item: PortalSearchResult,
  localePath: (path: string) => string,
  currentPath: string,
  language: string,
): RouteLocationRaw => {
  const id = item.sourceKey ?? item.id.split('-')[1]

  switch (item.entityType) {
    case 'risk-assessment':
      return localePath(`/portal/risk-management/risk-assessments/${id}/view`)
    case 'accepted-feed-material':
      return localePath(`/portal/risk-management/accepted-feed-materials/${id}`)
    case 'processing-aid':
      return localePath(`/portal/risk-management/processing-aids/${id}`)
    case 'undesirable-substance':
      return localePath(`/portal/risk-management/undesirable-substances/${id}`)
    case 'certified-company':
      return {
        path: localePath(`/portal/certified-companies/${id}`),
        query: { returnTo: currentPath },
      }
    case 'document':
      return localePath(
        `/portal/feed-scheme-documents/${generateSlug(item.title)}/${id}/${language}/`,
      )
    default:
      return '#'
  }
}

export const toPortalSearchHighlightSegments = (value?: string): PortalSearchHighlightSegment[] => {
  if (!value) return []

  const sanitized = dompurify.sanitize(value, {
    ALLOWED_TAGS: ['em'],
    ALLOWED_ATTR: [],
  })
  const parser = new DOMParser()
  const document = parser.parseFromString(`<div>${sanitized}</div>`, 'text/html')

  return Array.from(document.body.firstElementChild?.childNodes ?? [])
    .map((node) => ({
      text: node.textContent ?? '',
      emphasized: node.nodeType === Node.ELEMENT_NODE && node.nodeName.toLowerCase() === 'em',
    }))
    .filter((segment) => segment.text.length > 0)
}
